'use client';

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { Modal, Box, Grid, IconButton, Typography, Dialog, DialogContent, DialogTitle, AppBar, Toolbar, SwipeableDrawer } from '@mui/material';
import ImagePicker from '../components/ImagePicker';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, ArrowRight, ArrowLeft, ImageIcon, Trash2, ChevronLeft, X } from 'lucide-react';
import { useRecipe } from '../hooks/useRecipe';
import { useNavigate } from 'react-router-dom';
import food01 from '../mock/images/food01.jpeg';
import food02 from '../mock/images/food02.jpeg';
import food03 from '../mock/images/food03.jpeg';
import card1 from '../mock/images/card1.png';
import card2 from '../mock/images/card2.png';
import card3 from '../mock/images/card3.png';

import { Settings, Zap, Clock, Play, Layers, RotateCcw, Menu } from "lucide-react"
import CardsContext from '../context/CardsContext';
import { useTranslation } from 'react-i18next';



const convertImageToBase64 = (url: string): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Adjust the second argument (0.5) to your desired quality level
        const dataURL = canvas.toDataURL('image/jpeg', 0.5);
        resolve(dataURL);
      } else {
        reject(new Error('Failed to convert image to base64.'));
      }
    };
    img.onerror = () => {
      reject(new Error('Failed to load image.'));
    };
    img.src = url;
  });
};

const CameraCapture: React.FC = () => {
  const [photos, _setPhotos] = useState<string[]>([]);
  const webcamRef = useRef<Webcam>(null);
  const cardsContext = useContext(CardsContext);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    Promise.all([
      convertImageToBase64(card3),
    ])
      .then((base64Images) => {
        _setPhotos(base64Images);
      })
      .catch((error) => {
        console.error('Failed to convert images to base64:', error);
        // Add your code here to handle the error
      });
  }, []);

  const capturePhoto = useCallback(() => {
    if (webcamRef.current && photos.length < 4) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        _setPhotos((prevPhotos) => [...prevPhotos, imageSrc]);
      }
    }
  }, [photos]);

  const deletePhoto = (index: number) => {
    _setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
  };

  const handleNextStep = async () => {
    if (cardsContext) {
      cardsContext.setPhotos(photos);
      navigate('/reading-cards');
    }
  };

  const handleImageChange = async (event: any) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Get the first file
      const file = files[0];
      // Create a local URL of the file (for preview purposes)
      const imageUrl = await convertImageToBase64(URL.createObjectURL(file))

      _setPhotos((prevPhotos) => [...prevPhotos, imageUrl])
    }
  };

  const handleOpen = useCallback(() => {
    // This is intentionally empty as we're controlling the open state from the parent
  }, []);

  const CameraInterface: React.FC<{ webcamRef: React.RefObject<Webcam>, photos: string[] }> = ({ webcamRef, photos }) => {
    return (
      <div className="relative h-screen w-full bg-black text-white">
        {/* Top Controls */}
        <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white"
            onClick={() => navigate('/mode-selector')}
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>

        </div>

        {/* Camera Viewport */}
        <div className="relative h-full w-full">
          <div className="absolute inset-0">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode: 'environment',
              }}
              className="w-full h-full object-cover"
            />
            {/* Grid Overlay */}
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">

              <div className="border-r border-b border-white/30"></div>
              <div className="border-r border-b border-white/30"></div>
              <div className="border-b border-white/30"></div>
              <div className="border-r border-b border-white/30"></div>
              <div className="border-r border-b border-white/30"></div>
              <div className="border-b border-white/30"></div>
              <div className="border-r border-white/30"></div>
              <div className="border-r border-white/30"></div>
              <div></div>
            </div>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-6">
          <div className="flex justify-between items-center px-12">
            <div className="w-12 h-12 rounded-full overflow-hidden">
              <IconButton
                sx={{
                  width: 48,
                  height: 48,
                  padding: 0,
                  overflow: 'hidden',
                  '&:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
                onClick={() => setOpenModal(true)}
              >
                {photos[0] ? (
                  <img
                    src={photos[0]}
                    alt="Gallery Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                  />
                ) : (
                  <ImageIcon
                    className="w-full h-full p-2 text-white"
                  />
                )}
              </IconButton>
            </div>
            <button className="w-16 h-16 rounded-full border-4 border-white" onClick={capturePhoto}></button>
            <Button variant="ghost" size="icon" className="w-12 h-12 text-white" onClick={handleNextStep}
              disabled={photos.length > 1}>
              <ArrowRight className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Move SwipeableDrawer here, outside of other components */}
        <SwipeableDrawer
          anchor="bottom"
          open={openModal}
          onClose={() => setOpenModal(false)}
          onOpen={handleOpen}
          slotProps={{
            paper: {
              sx: {
                marginRight: '-1px',
                height: '80vh',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                backgroundColor: '#ECEAE5',
                zIndex: 1300,
                position: 'absolute',

              }
            }
          }}
          sx={{
            '& .MuiDrawer-paper': {
              overflow: 'visible'
            },
            zIndex: 1300
          }}
        >
          <Box sx={{
            width: '100%',
            height: '100%',
            padding: '20px',
            position: 'relative'
          }}>
            <h2 className="text-2xl font-bold mb-4">
              {t('cameraCapture.yourPhotos')}
              <IconButton
                aria-label="close"
                onClick={() => setOpenModal(false)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 0,
                }}
              >
                <X />
              </IconButton>
            </h2>


            <Box sx={{ overflowY: 'auto', height: 'calc(100% - 64px)' }}>
              <Grid container spacing={2}>
                {photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={photo}
                        alt={`${t('cameraCapture.galleryPreview')} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                      <IconButton
                        onClick={() => deletePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)',
                            color: 'error.main'
                          }
                        }}
                      >
                        <Trash2 />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {photos.length === 0 && (
                <Typography variant="body1" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                  {t('cameraCapture.noPhotosYet')}
                </Typography>
              )}
            </Box>
          </Box>
        </SwipeableDrawer>
      </div>
    )
  }
  return (
    <div className="flex flex-col h-full bt-0 bg-gray-100">
      <CameraInterface webcamRef={webcamRef} photos={photos} />
    </div>
  );
};

export default CameraCapture;
